
import React from 'react';
import { Box, Paper, Typography, Tooltip, Stack, Zoom } from '@mui/material';
import { CheckCircle, RadioButtonUnchecked, InfoOutlined } from '@mui/icons-material';
import { GenerationProgress } from '../utils/familyTreeUtils';
import { alpha, useTheme } from '@mui/material/styles';

interface GenerationReadinessViewProps {
  generationProgress?: GenerationProgress[];
}

const GENERATION_LABELS = [
  "You & Spouse",
  "Parents",
  "Grandparents",
  "Gt-Grandparents",
  "Gt-Gt-Grandparents"
];

const GenerationReadinessView: React.FC<GenerationReadinessViewProps> = ({ generationProgress }) => {
  const theme = useTheme();

  if (!generationProgress || generationProgress.length === 0) {
    return null;
  }

  // We only show up to 5 generations as that's what we have labels for and what usually matters most
  const displayGenerations = generationProgress.slice(0, 5);

  return (
    <Paper 
      elevation={0}
      sx={{ 
        p: 2, 
        mb: 2, 
        borderRadius: 2, 
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: alpha(theme.palette.background.paper, 0.6),
        backdropFilter: 'blur(8px)'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="subtitle2" color="text.secondary" fontWeight="bold">
          FAMILY TREE READINESS
        </Typography>
        
        {/* Readiness Message */}
        {(() => {
            let consecutiveComplete = 0;
            for (const gen of displayGenerations) {
                if (gen.completionPercentage === 100 && gen.imageCompletionPercentage === 100) {
                    consecutiveComplete++;
                } else {
                    break;
                }
            }

            if (consecutiveComplete >= 2) {
                return (
                    <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600, bgcolor: alpha(theme.palette.success.main, 0.1), px: 1, py: 0.5, borderRadius: 1 }}>
                        Ready for {consecutiveComplete}-Generation Orders!
                    </Typography>
                );
            } 
            return null;
        })()}

        <Box sx={{ flexGrow: 1 }} />
        
        <Tooltip title="Green checks indicate that all ancestors in that generation have names, birth dates, and photos.">
            <InfoOutlined fontSize="small" color="action" />
        </Tooltip>
      </Box>

      <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center" sx={{ overflowX: 'auto', pb: 1 }}>
        {displayGenerations.map((gen, index) => {
            const isComplete = gen.completionPercentage === 100 && gen.imageCompletionPercentage === 100;
            const label = GENERATION_LABELS[index] || `Gen ${index}`;
            const statusColor = isComplete ? theme.palette.success.main : theme.palette.text.disabled;
            const statusBg = isComplete ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.action.disabledBackground, 0.5);
            
            // Tooltip message
            let tooltipMsg = "Complete!";
            if (!isComplete) {
                const missingParts = [];
                if (gen.completionPercentage < 100) missingParts.push("dates");
                if (gen.imageCompletionPercentage < 100) missingParts.push("pictures");
                tooltipMsg = `Add ${missingParts.join(" and ")} to complete this generation.`;
            }

            return (
                <Tooltip 
                    key={gen.generation} 
                    title={tooltipMsg} 
                    arrow 
                    TransitionComponent={Zoom}
                >
                    <Box 
                        sx={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center',
                            cursor: 'help',
                            p: 1,
                            borderRadius: 2,
                            minWidth: 85,
                            transition: 'all 0.2s',
                            '&:hover': {
                                bgcolor: statusBg,
                                transform: 'translateY(-2px)'
                            }
                        }}
                    >
                        {isComplete ? (
                            <CheckCircle sx={{ color: statusColor, fontSize: 32, mb: 1 }} />
                        ) : (
                            <RadioButtonUnchecked sx={{ color: statusColor, fontSize: 32, mb: 1 }} />
                        )}
                        <Typography variant="caption" align="center" sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.2 }}>
                            {label}
                        </Typography>
                        <Typography variant="caption" sx={{ color: isComplete ? 'success.main' : 'text.secondary', fontSize: '0.7rem' }}>
                            {isComplete ? "100%" : `${Math.floor((gen.completionPercentage + gen.imageCompletionPercentage)/2)}%`}
                        </Typography>
                    </Box>
                </Tooltip>
            );
        })}
      </Stack>
    </Paper>
  );
};

export default GenerationReadinessView;
